/**
 * Impostazioni, sezione «Limiti e sicurezza»: quante modifiche al giorno, e
 * cosa decide Otto da solo.
 */

import { useEffect, useState } from "react";
import { Gauge, ShieldCheck } from "lucide-react";
import { CATEGORY_LABELS, AUTONOMY_UNLOCK_MIN_ACTIONS } from "@otto/shared";
import { api } from "../../lib/api.ts";
import { messaggioDi } from "../../lib/errori.ts";
import { useStore } from "../../lib/store.tsx";
import { Badge } from "../../ui/primitives.tsx";
import { Scheda } from "./comuni.tsx";

export function SezioneModifiche() {
  const store = useStore();
  const account = store.account;
  const [tetto, setTetto] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (account) setTetto(String(account.dailyWriteLimit));
  }, [account]);

  /**
   * Il tetto di modifiche al giorno. È una rete di sicurezza — «oltre questo
   * numero fermati» — e una rete che non si può stringere non è una rete.
   */
  const salvaTetto = async () => {
    const valore = Number(tetto.trim());
    if (!Number.isInteger(valore) || valore < 1 || valore > 1000) {
      store.notify("Il tetto dev'essere un numero intero fra 1 e 1000.");
      setTetto(String(account?.dailyWriteLimit ?? 50));
      return;
    }
    if (valore === account?.dailyWriteLimit) return;
    setBusy(true);
    try {
      await api.setDailyLimit(valore);
      await store.refreshAccount();
      store.notify(
        `Tetto a ${valore} modifiche al giorno. Arrivato lì, mi fermo fino a domani.`,
      );
    } catch (errore) {
      store.notify(messaggioDi(errore));
      setTetto(String(account?.dailyWriteLimit ?? 50));
    } finally {
      setBusy(false);
    }
  };

  const usate = account?.writesToday ?? 0;
  const limite = account?.dailyWriteLimit ?? 50;

  return (
    <>
      <Scheda
        Icona={Gauge}
        titolo="Tetto di modifiche al giorno"
        testo="Superato questo numero, Otto si ferma fino a domani. È la rete che ti protegge da una raffica di modifiche sbagliate."
      >
        <div className="flex flex-wrap items-center gap-4">
          <label htmlFor="tetto-giornaliero" className="sr-only">
            Tetto di modifiche al giorno
          </label>
          <input
            id="tetto-giornaliero"
            type="number"
            min={1}
            max={1000}
            value={tetto}
            disabled={busy}
            onChange={(event) => setTetto(event.target.value)}
            onBlur={salvaTetto}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
            }}
            className="w-24 rounded-field border border-line bg-surface px-3 py-2 text-[20px] font-bold tabular-nums text-ink focus:border-ink focus:outline-none"
          />
          <div className="min-w-[180px] flex-1">
            <div className="h-1.5 overflow-hidden rounded-pill bg-surface-sunken">
              <div
                className="h-full rounded-pill bg-ink transition-[width] duration-calm"
                style={{
                  width: `${Math.min(100, (usate / Math.max(1, limite)) * 100)}%`,
                }}
              />
            </div>
            <p className="mt-1.5 text-[12.5px] text-ink-faint">
              oggi ne hai usate {usate} su {limite}
            </p>
          </div>
        </div>
      </Scheda>

      <Scheda
        Icona={ShieldCheck}
        titolo="Autonomia"
        testo={`Otto propone e tu approvi. Quando un tipo di modifica supera l'85% di approvazione su almeno ${AUTONOMY_UNLOCK_MIN_ACTIONS} proposte, potrai lasciarglielo fare da solo.`}
      >
        <ul className="flex flex-col divide-y divide-line rounded-card border border-line">
          {store.autonomy.map((row) => (
            <li
              key={row.category}
              className="flex items-center gap-3 px-3.5 py-2.5"
            >
              <span className="flex-1 text-[13.5px] font-medium text-ink">
                {CATEGORY_LABELS[row.category]}
              </span>
              <span className="text-[12px] tabular-nums text-ink-faint">
                {row.total > 0
                  ? `${Math.round((row.approvalRate ?? 0) * 100)}% su ${row.total}`
                  : "nessun dato"}
              </span>
              <Badge tone={row.nextGearUnlockable ? "ok" : "plain"}>
                {row.nextGearUnlockable ? "sbloccabile" : "propone"}
              </Badge>
            </li>
          ))}
        </ul>
      </Scheda>
    </>
  );
}
